import React from 'react';
import { Control, Controller, UseFormSetValue, FieldErrors } from 'react-hook-form';

const OTPLength = 6;

interface TwoFactorAuthForm {
  code: string[]; // An array of strings for each digit of the OTP
}

interface OTPInputProps {
  rhfControl: Control<TwoFactorAuthForm>;
  containerClassName?: string;
  isDarkMode?: boolean;
  errors?: FieldErrors<TwoFactorAuthForm>;
  onSubmit: () => void;
  rhfSetValue: UseFormSetValue<TwoFactorAuthForm>;
}

const OTPInput: React.FC<OTPInputProps> = ({
  rhfControl,
  containerClassName,
  isDarkMode,
  errors,
  onSubmit,
  rhfSetValue,
}) => {
  const className = `mb-3 flex justify-between ${containerClassName}`;

  // Utility function to distribute OTP digits across all fields
  const distributeDigits = (digits: string, triggerSubmit: boolean = true) => {
    // Clear all fields first
    for (let i = 0; i < 6; i++) {
      rhfSetValue(`code.${i}`, '', { shouldValidate: false });
    }

    // Set each digit in its respective field starting from the beginning
    const digitsArray = digits.split('');
    digitsArray.forEach((digit, i) => {
      if (i < 6) {
        rhfSetValue(`code.${i}`, digit, { shouldValidate: true });
      }
    });

    // Focus on the last filled input or the next empty one
    const lastIndex = Math.min(digits.length - 1, 5);
    setTimeout(() => {
      const targetInput = document.querySelector<HTMLInputElement>(`input[name='code.${lastIndex}']`);
      targetInput?.focus();
    }, 0);

    // Auto-submit if we have exactly 6 digits and triggerSubmit is true
    if (digits.length === 6 && triggerSubmit) {
      setTimeout(() => {
        onSubmit();
      }, 150); // Slightly longer delay to ensure all fields are updated and DOM is stable
    }
  };

  const handleInputChange = (index: number, value: string) => {
    // Remove any non-digit characters and handle various input formats
    // This handles spaces, dashes, and other common OTP formatting characters
    // Also handles clipboard content with special characters like "Your code is: 123456"
    const cleanValue = value.replace(/[^0-9]/g, '');

    // Check if this is mobile OTP auto-fill, paste, or SMS auto-suggestion (multiple digits entered at once)
    // This covers:
    // 1. Mobile keyboard OTP suggestions (iOS/Android)
    // 2. Copy-paste from clipboard
    // 3. Browser auto-fill
    // 4. SMS auto-fill suggestions
    if (cleanValue.length > 1) {
      // This is likely mobile OTP auto-fill or paste - distribute the digits across all fields
      const digits = cleanValue.slice(0, 6); // Take up to 6 digits
      distributeDigits(digits);
      return;
    }

    // Regular single character input handling for manual typing
    const singleDigit = cleanValue.slice(0, 1);

    // Only proceed if the value is empty or a single digit
    if (singleDigit === '' || /^[0-9]$/.test(singleDigit)) {
      rhfSetValue(`code.${index}`, singleDigit, { shouldValidate: true });

      // Auto-advance to next field for single digit input
      if (singleDigit && index < 5) {
        const nextInput = document.querySelector<HTMLInputElement>(`input[name='code.${index + 1}']`);
        nextInput?.focus();
      }

      // Check if all digits are filled after this update (for manual typing completion)
      if (singleDigit && index === 5) {
        // Use setTimeout to ensure the form state is updated before checking
        setTimeout(() => {
          const allValues = Array.from({ length: 6 }).map((_, i) => {
            const input = document.querySelector<HTMLInputElement>(`input[name='code.${i}']`);
            return input?.value || '';
          });

          if (allValues.every((val) => /^[0-9]$/.test(val))) {
            onSubmit();
          }
        }, 0);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        if (index > 0) {
          e.preventDefault();
          const prevInput = document.querySelector<HTMLInputElement>(`input[name='code.${index - 1}']`);
          prevInput?.focus();
        }
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        if (index < 5) {
          e.preventDefault();
          const nextInput = document.querySelector<HTMLInputElement>(`input[name='code.${index + 1}']`);
          nextInput?.focus();
        }
        break;
      case 'Backspace':
        if (index > 0 && !e.currentTarget.value) {
          const prevInput = document.querySelector<HTMLInputElement>(`input[name='code.${index - 1}']`);
          prevInput?.focus();
        }
        break;
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    // Check if clipboardData is available
    if (!e.clipboardData) {
      console.warn('Clipboard data not available');
      return;
    }

    // Try to get data from multiple clipboard formats to handle different platforms
    let pastedData = '';

    // Try different clipboard data types for cross-platform compatibility
    try {
      pastedData =
        e.clipboardData.getData('text/plain') || e.clipboardData.getData('text') || e.clipboardData.getData('Text');
    } catch {
      // Fallback for older browsers or restricted clipboard access
      console.warn('Clipboard access limited, using fallback');
      try {
        pastedData = e.clipboardData.getData('text') || '';
      } catch {
        console.warn('All clipboard access methods failed');
        return;
      }
    }

    // Clean the pasted data - handle various OTP formats
    // This regex handles: "123456", "123 456", "123-456", "1 2 3 4 5 6", etc.
    const cleanValue = pastedData.replace(/[^0-9]/g, '').slice(0, 6);

    if (cleanValue.length === 0) return;

    // Use the centralized distribution function
    distributeDigits(cleanValue);
  };

  // Handle composition events for IME input (some mobile keyboards)
  const handleCompositionEnd = (index: number, e: React.CompositionEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    const cleanValue = value.replace(/[^0-9]/g, '');

    if (cleanValue.length > 1) {
      const digits = cleanValue.slice(0, 6);
      distributeDigits(digits);
    }
  };

  // Handle click to position cursor at the end of input value
  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    // Set selection range to position cursor at the end
    const valueLength = input.value.length;
    // Use setTimeout to ensure this happens after the default click behavior
    setTimeout(() => {
      input.setSelectionRange(valueLength, valueLength);
    }, 0);
  };

  return (
    <div className={className} role="group" aria-label="OTP input">
      {Array.from({ length: OTPLength }, (_, index) => (
        <Controller
          key={index}
          name={`code.${index}`}
          control={rhfControl}
          rules={{
            required: 'This field is required',
            pattern: {
              value: /^[0-9]$/,
              message: 'Only numbers are allowed',
            },
            maxLength: 1,
          }}
          render={({ field }) => (
            <input
              type="tel"
              maxLength={6} // Allow up to 6 digits for mobile auto-fill scenarios
              inputMode="numeric"
              pattern="[0-9]*"
              {...field}
              aria-label={`Digit ${index + 1} of ${OTPLength}`}
              className={`h-12 w-12 rounded-lg border ${
                !isDarkMode ? 'border-neutral-200 bg-neutral-100' : 'border-neutral-800 bg-neutral-700 text-white'
              } text-center outline-none ${
                errors.code?.[index]
                  ? isDarkMode
                    ? 'border-neutral-800'
                    : 'border-neutral-200'
                  : 'focus:border-therablue'
              } transition-colors duration-200`}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onCompositionEnd={(e) => handleCompositionEnd(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onClick={handleClick}
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              autoFocus={index === 0}
              value={field.value || ''}
              // Additional attributes for better mobile support
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              // Enhanced accessibility
              role="textbox"
              aria-describedby={`otp-input-${index}-desc`}
            />
          )}
        />
      ))}
    </div>
  );
};

export default OTPInput;
